import { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { USER_APPROVE_STATUS, USER_QUALIFY_STATUS } from '@/config/constants';
import { LOAN_PATH } from '@/config/loanNavPaths';
import { useGetDocumentReadinessSummaryQuery } from '@/apis/extended/documentRequirementsApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { ProfileMortgageApplicationPanel } from '@/components/Profile/ProfileMortgageApplicationPanel';

const steps = [
  {
    label: 'Profile Setup',
    description: 'Complete your personal information and loan preferences',
    status: 'completed',
  },
  {
    label: 'Document Upload',
    description: 'Upload required documents for loan processing',
    status: 'completed',
  },
  {
    label: 'Pre-Qualification',
    description: 'Initial assessment of your loan eligibility',
    status: 'active',
  },
  {
    label: 'Loan Approval',
    description: 'Final approval and loan amount determination',
    status: 'pending',
  },
  {
    label: 'Ready to Make Offers',
    description: 'Start shopping for properties and submitting offers',
    status: 'pending',
  },
];

export const ProfileLoanStatus = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: user } = useGetCurrentUserQuery();
  const { data: docReadiness } = useGetDocumentReadinessSummaryQuery(
    undefined,
    {
      skip: !user,
    }
  );
  const documentsStepComplete =
    docReadiness?.fullUserReady ?? Boolean(user?.documents?.length);

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex === 0) return 'completed';
    if (stepIndex === 1) return documentsStepComplete ? 'completed' : 'active';
    if (stepIndex === 2)
      return user?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY
        ? 'completed'
        : 'active';
    if (stepIndex === 3)
      return user?.approveStatus === USER_APPROVE_STATUS.APPROVED
        ? 'completed'
        : 'pending';
    if (stepIndex === 4)
      return user?.approveStatus === USER_APPROVE_STATUS.APPROVED
        ? 'completed'
        : 'pending';
    return 'pending';
  };

  const stepStatuses = useMemo(
    () => steps.map((_, index) => getStepStatus(index)),
    [user, documentsStepComplete]
  );
  const activeStep = useMemo(() => {
    const firstIncomplete = stepStatuses.findIndex((s) => s !== 'completed');
    if (firstIncomplete === -1) return steps.length - 1;
    return stepStatuses[firstIncomplete] === 'active'
      ? firstIncomplete
      : Math.max(0, firstIncomplete - 1);
  }, [stepStatuses]);

  /** Single step whose instructional caption is shown under the stepper. */
  const milestoneCaptionStepIndex = useMemo(() => {
    const activeIdx = stepStatuses.findIndex((s) => s === 'active');
    if (activeIdx !== -1) return activeIdx;
    const pendingIdx = stepStatuses.findIndex((s) => s === 'pending');
    if (pendingIdx !== -1) return pendingIdx;
    return steps.length - 1;
  }, [stepStatuses]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'active':
        return 'primary';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getNextAction = () => {
    if (!documentsStepComplete) {
      return {
        title: 'Upload Documents',
        description:
          'Upload required documents to continue with your loan application',
        action: 'Upload Now',
        icon: 'fa-light fa-upload',
      };
    }
    if (user?.qualifyStatus !== USER_QUALIFY_STATUS.QUALIFY) {
      return {
        title: 'Complete Pre-Qualification',
        description: 'Provide additional information for loan qualification',
        action: 'Continue',
        icon: 'fa-light fa-check-circle',
      };
    }
    if (user?.approveStatus !== USER_APPROVE_STATUS.APPROVED) {
      return {
        title: 'Awaiting Approval',
        description:
          "Your application is under review. We'll notify you once approved.",
        action: 'Check Status',
        icon: 'fa-light fa-clock',
      };
    }
    return {
      title: 'Ready to Shop',
      description:
        "You're approved! Start shopping for properties and make offers.",
      action: 'Start Shopping',
      icon: 'fa-light fa-house',
    };
  };

  const nextAction = getNextAction();

  const handlePrimaryAction = () => {
    if (!documentsStepComplete) {
      navigate(LOAN_PATH.documents);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Loan Application Progress */}
      <Grid item xs={12} lg={8} sx={{ minWidth: 0, maxWidth: '100%' }}>
        <Card
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            background: theme.palette.mainColors.background1,
            border: `1px solid ${theme.palette.greyShades.border1}`,
            maxHeight: { xs: 'min(52vh, 440px)', md: 'min(55vh, 520px)' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <CardHeader
            sx={{ flexShrink: 0, px: { xs: 2, sm: 3 } }}
            title="Loan Application Progress"
            avatar={<i className="fa-light fa-chart-line" />}
            titleTypographyProps={{
              sx: { color: theme.palette.text.primary, fontWeight: 600 },
            }}
          />
          <CardContent
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              minHeight: 0,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              pt: 0,
              px: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
              <Stepper
                orientation="horizontal"
                alternativeLabel
                activeStep={activeStep}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  pb: 1,
                  '& .MuiStep-root': {
                    minWidth: 0,
                    flex: '1 1 0',
                    px: 0.5,
                  },
                  '& .MuiStepLabel-root': {
                    alignItems: 'center',
                  },
                  '& .MuiStepLabel-labelContainer': {
                    width: '100%',
                    maxWidth: '100%',
                  },
                }}
              >
                {steps.map((step, index) => {
                  const status = stepStatuses[index];
                  return (
                    <Step
                      key={step.label}
                      completed={status === 'completed'}
                      active={status === 'active'}
                    >
                      <StepLabel
                        sx={{
                          '& .MuiStepLabel-label': {
                            color: theme.palette.text.primary,
                            fontWeight: status === 'active' ? 600 : 400,
                            width: '100%',
                          },
                          '& .MuiStepLabel-iconContainer': {
                            color: theme.palette.mainColors.primary,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            px: 0.25,
                          }}
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontWeight: 'inherit',
                              color: 'inherit',
                              textAlign: 'center',
                              lineHeight: 1.25,
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {step.label}
                          </Typography>
                          <Chip
                            label={
                              status.charAt(0).toUpperCase() + status.slice(1)
                            }
                            color={getStatusColor(status) as any}
                            size="small"
                            sx={{ maxWidth: '100%', height: 22 }}
                          />
                          {index === milestoneCaptionStepIndex && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.greyShades.grey1,
                                textAlign: 'center',
                                display: 'block',
                                width: '100%',
                                maxWidth: '100%',
                                lineHeight: 1.3,
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {step.description}
                            </Typography>
                          )}
                        </Box>
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Current Status & Actions */}
      <Grid item xs={12} lg={4}>
        <Card
          sx={{
            background: theme.palette.mainColors.background1,
            border: `1px solid ${theme.palette.greyShades.border1}`,
            mb: 3,
          }}
        >
          <CardHeader
            title="Current Status"
            avatar={<i className="fa-light fa-info-circle" />}
            titleTypographyProps={{
              sx: { color: theme.palette.text.primary, fontWeight: 600 },
            }}
          />
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ color: theme.palette.text.primary, mb: 1 }}
              >
                {nextAction.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.greyShades.grey1,
                  mb: nextAction.action === 'Start Shopping' ? 0 : 2,
                }}
              >
                {nextAction.description}
              </Typography>
              {nextAction.action !== 'Start Shopping' && (
                <Button
                  variant="contained"
                  startIcon={<i className={nextAction.icon} />}
                  fullWidth
                  sx={{ mt: 2, mb: 2 }}
                  onClick={handlePrimaryAction}
                >
                  {nextAction.action}
                </Button>
              )}
            </Box>

            <Divider
              sx={{ my: 2, borderColor: theme.palette.greyShades.border1 }}
            />

            <Box
              sx={{
                '& > .MuiPaper-root': {
                  mb: 0,
                  bgcolor: theme.palette.mainColors.background1,
                  border: `1px solid ${theme.palette.greyShades.border1}`,
                },
              }}
            >
              <ProfileMortgageApplicationPanel />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
